"use client";

import React from "react";
import { PiMapPin, PiPhone, PiEnvelope, PiArrowSquareOutBold } from "react-icons/pi";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";

export default function HokContact({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const handleUpdate = (field: string, value: any) => {
    onContentChange?.({
      ...content,
      [field]: value,
    });
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-8"
      style={{ fontFamily }}
    >
      {/* Header section (optional, hidden if not provided) */}
      {(content.title || content.subtitle || content.description) && (
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          {content.subtitle && (
            <span
              className="text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 block"
              style={{ color: primaryColor }}
            >
              <EditableText
                value={content.subtitle}
                onChange={(v) => handleUpdate("subtitle", v)}
                placeholder="Contact Us"
              />
            </span>
          )}
          {content.title && (
            <h2 className="text-2xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
              <EditableText
                value={content.title}
                onChange={(v) => handleUpdate("title", v)}
                placeholder="Get in Touch"
              />
            </h2>
          )}
          {content.description && (
            <p className="text-sm sm:text-base text-gray-600 whitespace-pre-line">
              <EditableText
                value={content.description}
                onChange={(v) => handleUpdate("description", v)}
                placeholder="We'd love to hear from you."
              />
            </p>
          )}
        </div>
      )}

      {/* Grid of 3 Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        {/* Location Card */}
        <div className="bg-white rounded-[24px] p-6 sm:p-7 shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border border-gray-50/50">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border transition-all duration-300"
            style={{ 
              borderColor: `${primaryColor}20`,
              backgroundColor: `${primaryColor}05`
            }}
          >
            <PiMapPin className="w-6 h-6" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-gray-900 tracking-tight leading-snug mb-1">
              <EditableText
                value={content.locationTitle || "Our Current Location"}
                onChange={(v) => handleUpdate("locationTitle", v)}
                placeholder="Our Current Location"
              />
            </h4>
            <div className="text-[13px] text-gray-500 leading-relaxed font-medium whitespace-pre-line mb-2">
              {onContentChange ? (
                <textarea
                  className="w-full text-[13px] text-gray-500 bg-transparent resize-none outline-none border-b border-dashed border-gray-300 focus:border-purple-400 leading-relaxed"
                  rows={2}
                  value={content.locationText || "4517 Washington Ave. Manchester,\nKentucky 39495, USA"}
                  onChange={(e) => handleUpdate("locationText", e.target.value)}
                  placeholder="Address here..."
                />
              ) : (
                (content.locationText || "4517 Washington Ave. Manchester,\nKentucky 39495, USA")
              )}
            </div>
            {content.locationMapsUrl && !onContentChange && (
              <a
                href={content.locationMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 transition-all hover:opacity-80"
                style={{ color: primaryColor, backgroundColor: `${primaryColor}10` }}
              >
                <PiMapPin className="w-3 h-3" />
                Open in Maps
                <PiArrowSquareOutBold className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* Phone Card */}
        <div className="bg-white rounded-[24px] p-6 sm:p-7 shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border border-gray-50/50">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border transition-all duration-300"
            style={{ 
              borderColor: `${primaryColor}20`,
              backgroundColor: `${primaryColor}05`
            }}
          >
            <PiPhone className="w-6 h-6" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-gray-900 tracking-tight leading-snug mb-1">
              <EditableText
                value={content.phoneTitle || "Phone Number"}
                onChange={(v) => handleUpdate("phoneTitle", v)}
                placeholder="Phone Number"
              />
            </h4>
            <div className="text-[13px] text-gray-500 leading-relaxed font-medium whitespace-pre-line">
              {onContentChange ? (
                <textarea
                  className="w-full text-[13px] text-gray-500 bg-transparent resize-none outline-none border-b border-dashed border-gray-300 focus:border-purple-400 leading-relaxed"
                  rows={2}
                  value={content.phoneText || "+(00) 12 - 345 6789\n+(00) 10 - 9876 543"}
                  onChange={(e) => handleUpdate("phoneText", e.target.value)}
                  placeholder="Phone numbers..."
                />
              ) : (
                (content.phoneText || "+(00) 12 - 345 6789\n+(00) 10 - 9876 543")
              )}
            </div>
          </div>
        </div>

        {/* Email Card */}
        <div className="bg-white rounded-[24px] p-6 sm:p-7 shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-lg transition-all duration-300 flex items-start gap-4 border border-gray-50/50">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border transition-all duration-300"
            style={{ 
              borderColor: `${primaryColor}20`,
              backgroundColor: `${primaryColor}05`
            }}
          >
            <PiEnvelope className="w-6 h-6" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-gray-900 tracking-tight leading-snug mb-1">
              <EditableText
                value={content.emailTitle || "Email"}
                onChange={(v) => handleUpdate("emailTitle", v)}
                placeholder="Email Address"
              />
            </h4>
            <div className="text-[13px] text-gray-500 leading-relaxed font-medium whitespace-pre-line">
              {onContentChange ? (
                <textarea
                  className="w-full text-[13px] text-gray-500 bg-transparent resize-none outline-none border-b border-dashed border-gray-300 focus:border-purple-400 leading-relaxed"
                  rows={2}
                  value={content.emailText || "team@example.com\nHoks@example.com"}
                  onChange={(e) => handleUpdate("emailText", e.target.value)}
                  placeholder="Emails..."
                />
              ) : (
                (content.emailText || "team@example.com\nHoks@example.com")
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
